import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map } from 'rxjs';
import { hasRoleCode, isPrivilegedUser } from '../auth/role.utils';
import { AuthStore } from '../stores/auth.store';
import { HOME_ROUTE, LOGIN_ROUTE } from './auth-route.constants';

/** Allows the route when the user has one of the given roles (ADMIN/ROOT always pass). */
export function roleGuard(...allowedRoles: string[]): CanActivateFn {
  return () => {
    const auth = inject(AuthStore);
    const router = inject(Router);

    return auth.waitUntilSessionReady().pipe(
      map(() => {
        const user = auth.user();
        if (!auth.isAuthenticated() || !user) {
          return router.createUrlTree([LOGIN_ROUTE]);
        }
        if (isPrivilegedUser(user) || hasRoleCode(user, ...allowedRoles)) {
          return true;
        }
        return deniedRedirect(router);
      }),
    );
  };
}

/** Allows the route only for staff (see `isPrivilegedUser`): the users who work across every tenant. */
export const staffGuard: CanActivateFn = roleGuard();

/** Allows the route only for Root-level users (role level 0). */
export const rootGuard: CanActivateFn = () => {
  const auth = inject(AuthStore);
  const router = inject(Router);

  return auth.waitUntilSessionReady().pipe(
    map(() => {
      const user = auth.user();
      if (!auth.isAuthenticated() || !user) {
        return router.createUrlTree([LOGIN_ROUTE]);
      }
      if (user.roles?.some((role) => role.level === 0)) {
        return true;
      }
      return deniedRedirect(router);
    }),
  );
};

function deniedRedirect(router: Router) {
  return router.createUrlTree([HOME_ROUTE]);
}
